import React, { useEffect } from "react";
import {
  BarChart, Bar,
  LineChart, Line,
  AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./StateViewCharts.css";

const COLORS = ["#003f5c", "#58508d", "#bc5090", "#ff6361", "#ffa600", "#00b894", "#2f4b7c"];

const StateViewCharts = ({
  tableData = [],
  dflData = [],
  ycData = [],
  citizenData = [],
  msmeData = [],
  hdData = [],
  hdTrainedData = [],
  trainersData = [],
  MSMEReachedData = [],
  benefitValueData = [], // ✅ New prop for Benefit Value Received
}) => {
  const saveVersionedData = (key, data) => {
    const versions = JSON.parse(localStorage.getItem(key)) || [];
    versions.push({ timestamp: new Date().toISOString(), data });
    localStorage.setItem(key, JSON.stringify(versions));
  };

  useEffect(() => {
    saveVersionedData("weeklyStateData", {
      tableData, dflData, ycData, citizenData, msmeData,
      hdData, hdTrainedData, trainersData, MSMEReachedData, benefitValueData
    });
  }, []);

  const generateInsights = (data, key) => {
    if (!data?.length) return {};
    const sorted = [...data].sort((a, b) => (b[key] || 0) - (a[key] || 0));
    return {
      topState: sorted[0]?.state || "N/A",
      topValue: sorted[0]?.[key] || 0,
      total: data.reduce((sum, item) => sum + (item[key] || 0), 0),
    };
  };

  const exportToPDF = async () => {
    const input = document.querySelector(".chart-container");
    const canvas = await html2canvas(input);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save("Insights_Report.pdf");
  };

  const Insights = ({ data, keys }) => (
    <div className="insight-section">
      {keys.map((key) => {
        const insight = generateInsights(data, key);
        return (
          <p key={key}>
            ➤ <strong>{key}</strong>: Top -{" "}
            <span className="highlight">{insight.topState}</span> (
            {insight.topValue}), Total - {insight.total}
          </p>
        );
      })}
    </div>
  );

  const renderLineChart = (data, keys, title, labels) => (
    <div className="chart-block">
      <h4>{title}</h4>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <XAxis dataKey="state" />
          <YAxis />
          <Tooltip />
          <Legend />
          {keys.map((key, idx) => (
            <Line key={key} dataKey={key} name={labels[idx]} strokeWidth={3} stroke={COLORS[idx % COLORS.length]} />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <Insights data={data} keys={keys} />
    </div>
  );

  const renderBarChart = (data, dataKey, title, color = "#8884d8") => (
    <div className="chart-block">
      <h4>{title}</h4>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ top: 20, right: 30, left: 80, bottom: 20 }}>
          <XAxis dataKey="state" />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey={dataKey} fill={color} />
        </BarChart>
      </ResponsiveContainer>
      <Insights data={data} keys={[dataKey]} />
    </div>
  );
  

  const renderAreaChart = (data, dataKey, title, color = "#7a5195") => (
    <div className="chart-block">
      <h4>{title}</h4>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={data}>
          <XAxis dataKey="state" />
          <YAxis />
          <Tooltip />
          <Area type="monotone" dataKey={dataKey} stroke={color} fill={color} />
        </AreaChart>
      </ResponsiveContainer>
      <Insights data={data} keys={[dataKey]} />
    </div>
  );

  const renderPieChart = (data, dataKey, title) => {
    const filtered = data.filter(d => d[dataKey]);
    return (
      <div className="chart-block">
        <h4>{title}</h4>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Tooltip />
            <Legend />
            <Pie
              data={filtered}
              dataKey={dataKey}
              nameKey="state"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label
            >
              {filtered.map((_, index) => (
                <Cell key={index} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <Insights data={data} keys={[dataKey]} />
      </div>
    );
  };

  return (
    <div>
      <div className="button-container">
        <button className="export-button" onClick={exportToPDF}>
          📄 Export to PDF
        </button>
      </div>

      <div className="chart-container">
        {tableData.length > 0 &&
          renderLineChart(tableData, ["total_applications_open_DI", "total_applications_submitted_DI", "total_br_DI"], "DI - Open, DS, BR", ["Open", "DS", "BR"])}

        {dflData.length > 0 &&
          renderAreaChart(dflData, "total_applications_open_DFL", "DFL - Open Applications", "#bc5090")}

        {ycData.length > 0 &&
          renderBarChart(ycData, "total_applications_open_YC", "YC - Open Applications", "#ffa600")}

        {citizenData.length > 0 &&
          renderPieChart(citizenData, "total_citizen_screened", "Total Citizen Screened")}

        {msmeData.length > 0 &&
          renderBarChart(msmeData, "total_applications_open_DI", "MSME - Total Open Applications", "#7a5195")}

        {hdData.length > 0 &&
          renderAreaChart(hdData, "Total Active HD", "Total Active HDs", "#2f4b7c")}

        {hdTrainedData.length > 0 &&
          renderBarChart(hdTrainedData, "Total Haqdarshaks Trained", "Haqdarshaks Trained", "#ef5675")}

        {trainersData.length > 0 &&
          renderAreaChart(trainersData, "Total Trainers Trained", "Trainers Trained", "#003f5c")}

        {MSMEReachedData.length > 0 &&
          renderPieChart(MSMEReachedData, "total_msme", "MSME Reached")}

        {/* ✅ Benefit Value Received - Bar Chart */}
        {benefitValueData.length > 0 &&
          renderBarChart(
            benefitValueData,
            "Total Benefits Value Received - Citizen",
            "Benefit Value Received by State",
            "#00b894"
          )}

        
      </div>
    </div>
  );
};

export default StateViewCharts;
